import React, { FC, useEffect, useState } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Row,
  Col,
  Timeline,
  Badge,
  Button,
  Empty,
  Dropdown,
  message,
  Popover,
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  FlagOutlined,
  TagsOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  EditOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  DownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  BugOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import Swal, { SweetAlertIcon } from "sweetalert2";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { getColorByLabel } from "../projects/ProjectInformationById";
import { issueApis } from "../../../api/Issue";
import { workApprovalApis } from "../../../api/WorkApproval";
import { updateIssueInDetailModalSlice } from "../../../store/slices/projectManagement/issuee-view/IssueInDetailView";

interface IssueQuickViewCardProps {
  onEdit?: () => void;
  showEditButton?: boolean;
  onRefresh?: () => void;
}

const getStatusColor = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "inprogress" || s === "in progress")
    return "processing";
  if (s === "completed" || s === "closed" || s === "resolved" || s === "approved")
    return "success";
  if (s === "onhold" || s === "on hold") return "warning";
  if (s === "cancelled" || s === "deleted" || s === "rejected") return "error";
  if (s === "open" || s === "draft" || s === "yettostart") return "default";
  return "default";
};

const getPriorityColor = (priority: string) => {
  const p = (priority || "").toLowerCase();
  if (p === "high" || p === "critical" || p === "urgent") return "red";
  if (p === "medium") return "orange";
  if (p === "low") return "blue";
  return "default";
};

const formatDate = (d: any) =>
  d ? dayjs(d).format("DD MMM YYYY") : "Not Set";

const ISSUE_STATUS_ORDER = [
  "Open",
  "InProgress",
  "Resolved",
  "UnderQC",
  "Closed",
];

const IssueQuickViewCard: FC<IssueQuickViewCardProps> = ({
  onEdit,
  showEditButton = true,
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const [messageApi, msgContext] = message.useMessage();
  const { data: IssueListInDetailModal } = useSelector(
    (state: RootState) => state.IssueListInDetailModal
  );
  const { data: currentUser } = useSelector(
    (state: RootState) => (state as any).currentUser
  );

  const [statusList, setStatusList] = useState<
    { label: string; key: string; value: string }[]
  >([]);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [isApprovalProcessing, setIsApprovalProcessing] = useState(false);

  const issue: any = IssueListInDetailModal?.selectedIssueDetails;

  // Load issue status list once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await issueApis.getStatus();
        const { data, status } = response || {};
        if (status && Array.isArray(data) && mounted) {
          const ordered = ISSUE_STATUS_ORDER.map((s) => {
            const match = data.find(
              (d: string) => d && d.toLowerCase() === s.toLowerCase()
            );
            return match ? { label: match, key: match, value: match } : null;
          }).filter(Boolean) as {
            label: string;
            key: string;
            value: string;
          }[];
          const rest = data
            .filter(
              (s: string) =>
                !ISSUE_STATUS_ORDER.some(
                  (o) => o.toLowerCase() === (s || "").toLowerCase()
                )
            )
            .map((s: string) => ({ label: s, key: s, value: s }));
          setStatusList([...ordered, ...rest]);
        }
      } catch {
        // silently ignore — dropdown just won't show
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const performStatusChange = async (newStatus: string) => {
    if (!issue?.issueId) return;
    setStatusUpdating(true);
    try {
      const response = await issueApis.changeIssueStatus(
        issue.issueId,
        newStatus,
        ""
      );
      const { status, message: msg } = response || {};
      if (status) {
        Swal.fire({
          icon: "success" as SweetAlertIcon,
          title: "Status Updated",
          text: msg || `Issue status updated to ${newStatus} successfully!`,
          confirmButtonText: "Ok",
        });
        dispatch(
          updateIssueInDetailModalSlice({
            selectedIssueDetails: {
              ...issue,
              issueStatus: newStatus,
            },
          })
        );
        onRefresh?.();
      } else {
        Swal.fire({
          icon: "error" as SweetAlertIcon,
          title: "Error",
          text: msg || "Failed to update status.",
          confirmButtonText: "Ok",
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update status. Please try again.",
        confirmButtonText: "Ok",
      });
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleApproveReject = async (
    action: "approve" | "reject",
    reason?: string
  ) => {
    if (!issue?.approvalLinkId) return;
    setIsApprovalProcessing(true);
    try {
      const baseRequestData = {
        projectId: issue.projectId,
        data: [
          {
            approvalLinkId: issue.approvalLinkId,
            parentModule: "Issue" as const,
          },
        ],
      };
      const response: any =
        action === "approve"
          ? await workApprovalApis.approveWorkItem(baseRequestData)
          : await workApprovalApis.rejectWorkItem({
              ...baseRequestData,
              reason: reason || "",
            });
      const { status, message: msg } = response || {};
      if (status) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text:
            response?.data?.message ||
            msg ||
            `Successfully ${action === "approve" ? "approved" : "rejected"} issue`,
          confirmButtonText: "OK",
        });
        onRefresh?.();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text:
            response?.data?.message ||
            response?.response?.data?.message ||
            msg ||
            `Failed to ${action} item. Please try again.`,
          confirmButtonText: "OK",
        });
      }
    } catch (err: any) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          `Failed to ${action} item.`,
        confirmButtonText: "OK",
      });
    } finally {
      setIsApprovalProcessing(false);
    }
  };

  const promptReject = async () => {
    const { value: reason } = await Swal.fire({
      title: "Reject Approval",
      input: "textarea",
      inputLabel: "Reason for rejection",
      inputPlaceholder: "Type your reason here...",
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value) return "Please input your reason!";
        return null;
      },
    });
    if (reason) handleApproveReject("reject", reason);
  };

  const handleStatusMenuClick = ({ key }: { key: string }) => {
    const newStatus = key;
    if (!newStatus || newStatus === issue?.issueStatus) return;
    Swal.fire({
      title: `Change status to ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        performStatusChange(newStatus);
      }
    });
  };

  if (!issue) {
    return (
      <div style={{ padding: 40 }}>
        <Empty description="No issue data available" />
      </div>
    );
  }

  // Permission gate for status change: only assignee or admin can change.
  const isAdmin = (currentUser?.genericRole || "").toLowerCase() === "admin";
  const isAssignee =
    !!currentUser?.id && Number(issue.assignedTo) === Number(currentUser.id);
  const statusChangeAllowed = !["closed", "archived"].includes(
    (issue.issueStatus || "").toLowerCase()
  );
  const canChangeStatus = statusChangeAllowed && (isAssignee || isAdmin);

  // Approve/reject permission: pending approvals for this user, or admin override.
  const isApprover =
    !!issue.approvalLinkId &&
    Array.isArray(issue.stateApprovals) &&
    (issue.stateApprovals.some(
      (a: any) => a.userId === currentUser?.id && a.status === "Pending"
    ) ||
      (isAdmin &&
        issue.stateApprovals.some((a: any) => a.status === "Pending")));

  const approvalChain: any[] = Array.isArray(issue.stateApprovalHierarchy)
    ? issue.stateApprovalHierarchy
    : [];

  const ApprovalHierarchyPopover = () => (
    <Popover
      trigger={["hover", "click"]}
      placement="right"
      title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
      content={
        <Timeline className="approval-timeline">
          {approvalChain.map((approval: any, index: number) => {
            const status = approval.approvalStatus || approval.status;
            const isApproved = status === "Approved";
            const isRejected = status === "Rejected";
            return (
              <Timeline.Item
                key={approval.stateApprovalHierarchyId ?? index}
                dot={
                  isApproved ? (
                    <CheckCircleOutlined style={{ color: "green" }} />
                  ) : isRejected ? (
                    <CloseCircleOutlined style={{ color: "red" }} />
                  ) : undefined
                }
              >
                {approval?.userName}
                <div className="designation">{approval?.roleName}</div>
                {approval?.approvalStage && (
                  <div className="designation">{approval?.approvalStage}</div>
                )}
                {approval?.level && (
                  <div className="designation">Level: {approval?.level}</div>
                )}
                {status && status !== "Active" && (
                  <div className="designation">Status: {status}</div>
                )}
                {approval?.actionDate && (
                  <div className="designation">
                    {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                  </div>
                )}
              </Timeline.Item>
            );
          })}
        </Timeline>
      }
    >
      <span
        style={{
          color: "#2557d6",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        Yes
      </span>
    </Popover>
  );

  const tagsCount = (issue.issueTags || []).length;
  const linksCount = (issue.issueLinks || []).length;
  const hasAutoApprovalRemarks =
    issue.approvalRequired === false && !!issue.autoApprovalRemarks;

  // Pending status change pushed by other modules (e.g. a Timelog edit that
  // asks the issue to move to Closed). Shown inline next to the current
  // status as `Current → New`.
  const pendingUpdates = issue.updates;
  const hasPendingStatusChange =
    !!pendingUpdates?.issueStatus &&
    String(pendingUpdates.issueStatus).toLowerCase() !==
      String(issue.issueStatus || "").toLowerCase();

  return (
    <div style={{ padding: "20px 24px" }}>
      {msgContext}
      {/* Status & quick action row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            rowGap: 6,
            columnGap: 8,
          }}
        >
          {canChangeStatus && statusList.length > 0 ? (
            <Dropdown
              menu={{
                items: statusList,
                selectedKeys: [issue.issueStatus],
                onClick: handleStatusMenuClick,
              }}
              trigger={["click"]}
              placement="bottomLeft"
              disabled={statusUpdating}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "2px 10px",
                  borderRadius: 12,
                  background: "#f5f5f5",
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: "20px",
                  cursor: statusUpdating ? "wait" : "pointer",
                  border: "1px dashed #d9d9d9",
                }}
                title="Click to change status"
              >
                <Badge status={getStatusColor(issue.issueStatus)} />
                {issue.issueStatus || "N/A"}
                <DownOutlined style={{ fontSize: 9, color: "#666" }} />
              </span>
            </Dropdown>
          ) : (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "2px 10px",
                borderRadius: 12,
                background: "#f5f5f5",
                fontSize: 12,
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              <Badge status={getStatusColor(issue.issueStatus)} />
              {issue.issueStatus || "N/A"}
            </span>
          )}
          {hasPendingStatusChange && (
            <>
              <ArrowRightOutlined
                style={{ color: "#999", fontSize: 11 }}
              />
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "2px 10px",
                  borderRadius: 12,
                  background: "#fff7e6",
                  border: "1px dashed #faad14",
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: "20px",
                  color: "#874d00",
                }}
                title="Pending status change"
              >
                <Badge status={getStatusColor(pendingUpdates.issueStatus)} />
                {pendingUpdates.issueStatus}
              </span>
            </>
          )}
          {issue.priorityName && (
            <Tag
              color={getPriorityColor(issue.priorityName)}
              style={{ margin: 0 }}
            >
              <FlagOutlined /> {issue.priorityName}
            </Tag>
          )}
          {issue.issueType && issue.issueType !== "None" && (
            <Tag color="purple" style={{ margin: 0 }}>
              <BugOutlined /> {issue.issueType}
            </Tag>
          )}
          {issue.flag && (
            <Tag color="gold" style={{ margin: 0 }}>
              {issue.flag}
            </Tag>
          )}
          {issue.approvalRequired === false && (
            <Tag
              color="gold"
              icon={<SafetyCertificateOutlined />}
              style={{ margin: 0 }}
            >
              No Approval
            </Tag>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {isApprover && (
            <>
              <Button
                size="small"
                style={{
                  backgroundColor: "#d1f5ec",
                  color: "#107154",
                  borderColor: "#d1f5ec",
                  fontWeight: 500,
                }}
                loading={isApprovalProcessing}
                onClick={() => handleApproveReject("approve")}
              >
                Approve
              </Button>
              <Button
                size="small"
                style={{
                  backgroundColor: "#fbe3e4",
                  color: "#a94442",
                  borderColor: "#fbe3e4",
                  fontWeight: 500,
                }}
                loading={isApprovalProcessing}
                onClick={promptReject}
              >
                Reject
              </Button>
            </>
          )}
          {showEditButton &&
            !["closed", "archived", "cancelled", "deleted"].includes(
              (issue?.issueStatus || "").toLowerCase()
            ) && (
              <Button
                type="primary"
                ghost
                size="small"
                icon={<EditOutlined />}
                onClick={onEdit}
              >
                Edit
              </Button>
            )}
        </div>
      </div>

      {/* Description */}
      {issue.issueDescription && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <FileTextOutlined style={{ marginRight: 8 }} />
              Description
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          headStyle={{ borderBottom: "1px solid #f0f0f0", minHeight: 40 }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div
            style={{
              fontSize: 13,
              color: "#4b5563",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {issue.issueDescription}
          </div>
        </Card>
      )}

      {/* Issue Details */}
      <Card
        size="small"
        title={
          <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
            Issue Details
          </span>
        }
        style={{ borderRadius: 12, marginBottom: 20 }}
        headStyle={{ borderBottom: "1px solid #f0f0f0", minHeight: 40 }}
        bodyStyle={{ padding: "12px 16px" }}
      >
        <Descriptions
          column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
          size="small"
          labelStyle={{
            fontWeight: 500,
            color: "#666",
            width: 150,
            whiteSpace: "nowrap",
          }}
        >
          <Descriptions.Item
            label={
              <>
                <UserOutlined style={{ marginRight: 6 }} />
                Assigned To
              </>
            }
          >
            {issue.IssueAssignedToName || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <UserOutlined style={{ marginRight: 6 }} />
                Reported By
              </>
            }
          >
            {issue.IssueAssignedByName || issue.createdByUserName || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Start Date
              </>
            }
          >
            {formatDate(issue.issueStartDate)}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Deadline
              </>
            }
          >
            {formatDate(issue.deadlineDate)}
          </Descriptions.Item>
          {issue.releasePhaseName && (
            <Descriptions.Item
              label={
                <>
                  <EnvironmentOutlined style={{ marginRight: 6 }} />
                  Release Phase
                </>
              }
            >
              {issue.releasePhaseName}
            </Descriptions.Item>
          )}
          {issue.taskName && (
            <Descriptions.Item
              label={
                <>
                  <LinkOutlined style={{ marginRight: 6 }} />
                  Linked Task
                </>
              }
            >
              {issue.taskName}
            </Descriptions.Item>
          )}
          <Descriptions.Item
            label={
              <>
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                Created
              </>
            }
            span={2}
          >
            {issue.createdAt
              ? dayjs(issue.createdAt).format("DD MMM YYYY")
              : issue.issueCreatedAt
              ? dayjs(issue.issueCreatedAt).format("DD MMM YYYY")
              : "N/A"}
            {issue.createdByUserName && (
              <span style={{ color: "#999", marginLeft: 8 }}>
                by {issue.createdByUserName}
              </span>
            )}
          </Descriptions.Item>
          {issue.approvalRequired !== undefined && (
            <Descriptions.Item
              label={
                <>
                  <SafetyCertificateOutlined style={{ marginRight: 6 }} />
                  Approval Required
                </>
              }
            >
              {issue.approvalRequired && approvalChain.length > 0 ? (
                <ApprovalHierarchyPopover />
              ) : (
                <Tag color={issue.approvalRequired ? "blue" : "default"}>
                  {issue.approvalRequired ? "Yes" : "No"}
                </Tag>
              )}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Tags */}
      {tagsCount > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <TagsOutlined style={{ marginRight: 8 }} />
              Tags{" "}
              <span style={{ color: "#999", fontWeight: 400 }}>
                ({tagsCount})
              </span>
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          headStyle={{ borderBottom: "1px solid #f0f0f0", minHeight: 40 }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {(issue.issueTags || []).map((tag: any, index: number) => {
              const color = getColorByLabel(tag.tagName || "");
              return (
                <Tag
                  key={tag.issueTagId ?? tag.tagId ?? index}
                  style={{
                    backgroundColor: color,
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "2px 10px",
                  }}
                >
                  {tag.tagName}
                </Tag>
              );
            })}
          </div>
        </Card>
      )}

      {/* Linked Issues */}
      {linksCount > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <LinkOutlined style={{ marginRight: 8 }} />
              Linked Issues{" "}
              <span style={{ color: "#999", fontWeight: 400 }}>
                ({linksCount})
              </span>
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          headStyle={{ borderBottom: "1px solid #f0f0f0", minHeight: 40 }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <Row gutter={[8, 8]}>
            {(issue.issueLinks || []).map((link: any, i: number) => (
              <Col xs={24} md={12} key={i}>
                <div
                  style={{
                    fontSize: 12,
                    padding: "6px 10px",
                    background: "#f5f5f5",
                    borderRadius: 4,
                    border: "1px solid #e8e8e8",
                  }}
                >
                  <span style={{ fontWeight: 500, color: "#1a1a2e" }}>
                    {link.linkType || "Related"}:
                  </span>{" "}
                  <span>{link.linkedIssueName || link.issueName || "—"}</span>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Resolution */}
      {issue.issueResolution && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#389e0d" }}>
              <CheckCircleOutlined style={{ marginRight: 8 }} />
              Resolution
            </span>
          }
          style={{
            borderRadius: 12,
            marginBottom: 20,
            background: "#f6ffed",
            border: "1px solid #b7eb8f",
          }}
          headStyle={{
            borderBottom: "1px solid #d9f7be",
            minHeight: 40,
            background: "#f6ffed",
          }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div
            style={{
              fontSize: 13,
              color: "#1f5132",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {issue.issueResolution}
          </div>
        </Card>
      )}

      {/* No-Approval Remarks */}
      {hasAutoApprovalRemarks && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#d48806" }}>
              <SafetyCertificateOutlined style={{ marginRight: 8 }} />
              No-Approval Remarks
            </span>
          }
          style={{
            borderRadius: 12,
            marginBottom: 20,
            background: "#fffbe6",
            border: "1px solid #ffe58f",
          }}
          headStyle={{
            borderBottom: "1px solid #ffe58f",
            minHeight: 40,
            background: "#fffbe6",
          }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={{ fontSize: 13, color: "#664d03", lineHeight: 1.6 }}>
            {issue.autoApprovalRemarks}
          </div>
        </Card>
      )}
    </div>
  );
};

export default IssueQuickViewCard;
